import { driveImageCandidateUrls, extractGoogleDriveFileId } from "./driveImageUrl";

/** Split one sheet cell into multiple URLs (pipe, semicolon, or newline). */
export function splitSheetMultiValue(raw) {
  if (raw == null || raw === "") return [];
  const s = String(raw).trim();
  if (!s) return [];
  return s
    .split(/\r?\n|\||,\s*(?=https?:\/\/)/i)
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * @param {string} url
 * @returns {{ provider: string, embedUrl: string, originalUrl: string } | null}
 */
export function parseVideoEmbed(url) {
  const originalUrl = String(url || "").trim();
  if (!originalUrl) return null;
  const u = originalUrl;
  const lower = u.toLowerCase();

  let m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (m) {
    return {
      provider: "youtube",
      embedUrl: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0`,
      originalUrl: u,
    };
  }
  m = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/i);
  if (m) {
    return {
      provider: "youtube",
      embedUrl: `https://www.youtube-nocookie.com/embed/${m[1]}?rel=0`,
      originalUrl: u,
    };
  }

  m = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (m) {
    return {
      provider: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${m[1]}`,
      originalUrl: u,
    };
  }

  if (/\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(lower)) {
    return { provider: "file", embedUrl: u, originalUrl: u };
  }

  const driveId = extractGoogleDriveFileId(u);
  if (driveId && (lower.includes("drive.google.com") || lower.includes("googleusercontent.com"))) {
    /** Order matters: Drive often returns HTML for large files on first URL — try several before iframe */
    const streamUrls = [
      `https://drive.google.com/uc?export=download&id=${driveId}`,
      `https://drive.google.com/uc?id=${driveId}&export=download`,
      `https://docs.google.com/uc?export=download&id=${driveId}`,
      `https://drive.google.com/uc?export=download&id=${driveId}&confirm=t`,
      `https://drive.google.com/uc?export=view&id=${driveId}`,
      `https://lh3.googleusercontent.com/d/${driveId}`,
    ];
    return {
      provider: "drive",
      embedUrl: `https://drive.google.com/file/d/${driveId}/preview`,
      streamUrls,
      streamUrl: streamUrls[0],
      streamUrlAlt: streamUrls[1],
      driveFileId: driveId,
      originalUrl: u,
    };
  }

  return null;
}

/** MIME hint for `<source type>` — helps the browser pick a decoder for .mov etc. */
export function getVideoMimeTypeForUrl(url) {
  const lower = String(url || "").split("?")[0].toLowerCase();
  if (lower.endsWith(".mov")) return "video/quicktime";
  if (lower.endsWith(".webm")) return "video/webm";
  if (lower.endsWith(".ogg") || lower.endsWith(".ogv")) return "video/ogg";
  if (lower.endsWith(".m4v")) return "video/x-m4v";
  return "video/mp4";
}

/** Strip hint fragment before loading as image / extracting Drive id (Drive ignores it). */
export function stripGalleryUrlHint(part) {
  return String(part || "")
    .trim()
    .replace(/#(asvideo|video)$/i, "");
}

/** Best-effort photo cell from row (opensheet / sheet header quirks). */
export function resolvePhotoRaw(row, legacyPhotoCell) {
  const tryKeys = [
    "photo links",
    "photo link(s)",
    "photo link",
    "photo_link",
    "photo link url",
    "photolink",
    "image link",
    "image links",
    "image link(s)",
  ];
  for (const k of tryKeys) {
    const v = row[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  for (const [k, v] of Object.entries(row || {})) {
    if (v == null || !String(v).trim()) continue;
    const kn = String(k).toLowerCase();
    if (/\bphoto\b.*\blink\b|\blink\b.*\bphoto\b|photolink|imagelink/i.test(kn.replace(/\s+/g, " "))) {
      return String(v).trim();
    }
  }
  if (legacyPhotoCell != null && String(legacyPhotoCell).trim()) {
    return String(legacyPhotoCell).trim();
  }
  return "";
}

/** Video URL cell — header may be "Video link(s)" → normalized key `video link(s)`, not `video links`. */
export function resolveVideoRaw(row, legacyVideoCell) {
  const tryKeys = [
    "video links",
    "video link(s)",
    "video link",
    "video_link",
    "video links url",
    "videolink",
    "product video",
    "product videos",
  ];
  for (const k of tryKeys) {
    const v = row[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  for (const [k, v] of Object.entries(row || {})) {
    if (v == null || !String(v).trim()) continue;
    const kn = String(k).toLowerCase().replace(/\s+/g, " ");
    if (/\bvideo\b.*\blink\b|\blink\b.*\bvideo\b|videolink/i.test(kn)) {
      return String(v).trim();
    }
  }
  if (legacyVideoCell != null && String(legacyVideoCell).trim()) {
    return String(legacyVideoCell).trim();
  }
  return "";
}

/** Customisation-only photo (lid editor) — separate from shop/product gallery. */
export function resolveCustomisationImageRaw(row, legacyCell) {
  const tryKeys = [
    "customisation image",
    "customization image",
    "customise image",
    "customize image",
    "customisation screen image",
    "customization screen image",
    "customisation photo",
    "customization photo",
    "lid image",
    "gift box customisation image",
  ];
  for (const k of tryKeys) {
    const v = row[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  for (const [k, v] of Object.entries(row || {})) {
    if (v == null || !String(v).trim()) continue;
    const kn = String(k).toLowerCase().replace(/\s+/g, " ");
    if (
      /\bcustomi[sz]ation\b.*\bimage\b|\bcustomi[sz]e\b.*\bimage\b|\blid\b.*\bimage\b/i.test(
        kn,
      )
    ) {
      return String(v).trim();
    }
  }
  if (legacyCell != null && String(legacyCell).trim()) {
    return String(legacyCell).trim();
  }
  return "";
}

/**
 * 1-based index into Photo link(s) images for /customise when no dedicated URL is set.
 * Column examples: "Customisation photo #", "Customise image #".
 */
export function resolveCustomisationPhotoIndex(row, legacyCell) {
  const tryKeys = [
    "customisation photo #",
    "customization photo #",
    "customise photo #",
    "customize photo #",
    "customisation image #",
    "customization image #",
    "customise image #",
    "customize image #",
    "customisation photo number",
    "customize photo number",
  ];
  for (const k of tryKeys) {
    const v = row[k];
    const n = parsePhotoIndex(v);
    if (n != null) return n;
  }
  for (const [k, v] of Object.entries(row || {})) {
    if (v == null || !String(v).trim()) continue;
    const kn = String(k).toLowerCase().replace(/\s+/g, " ");
    if (
      /\bcustomi[sz](?:ation|e)\b.*\b(?:photo|image)\b.*#|\b(?:photo|image)\b.*#.*\bcustomi[sz]/i.test(
        kn,
      ) ||
      /\bcustomi[sz](?:ation|e)\b.*\b(?:photo|image)\s*(?:#|number|no|index)\b/i.test(kn)
    ) {
      const n = parsePhotoIndex(v);
      if (n != null) return n;
    }
  }
  if (legacyCell != null && String(legacyCell).trim()) {
    return parsePhotoIndex(legacyCell);
  }
  return null;
}

function parsePhotoIndex(value) {
  const n = Math.round(Number(String(value ?? "").replace(/,/g, "").trim()));
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

function imageUrlsAtGalleryIndex(imageGroups, index) {
  const groups = Array.isArray(imageGroups) ? imageGroups : [];
  const urls = groups[index - 1];
  return Array.isArray(urls) && urls.length ? urls : [];
}

/**
 * Drive candidate URLs for the customise flow.
 * 1) Dedicated "Customisation image" URL in the sheet row.
 * 2) Else "Customisation photo #" (1-based index into Photo link(s) stills).
 * Returns [] when unset — callers fall back to product.images.
 */
export function buildCustomisationImageUrls(row, legacyUrlCell, imageGroups, legacyIndexCell) {
  const raw = resolveCustomisationImageRaw(row, legacyUrlCell);
  const parts = splitSheetMultiValue(raw);
  if (parts.length) {
    const first = stripGalleryUrlHint(parts[0]);
    if (first && !isGalleryEmbedPart(first)) {
      const urls = driveImageCandidateUrls(first);
      if (urls.length) return urls;
    }
  }

  const photoIndex = resolveCustomisationPhotoIndex(row, legacyIndexCell);
  if (photoIndex != null) {
    return imageUrlsAtGalleryIndex(imageGroups, photoIndex);
  }

  return [];
}

/** Prefer sheet customisation image on /customise; otherwise product gallery stills. */
export function productCustomiseImageUrls(product) {
  if (product?.customisationImages?.length) return product.customisationImages;
  return product?.images || [];
}

/**
 * Embed in gallery (not <img>): YouTube, Vimeo, direct video file, or Drive file when URL ends with #video or #asvideo.
 * Plain Drive /file/d/... links stay as images (treating all Drive as embed broke photos + shop thumbnails).
 */
function isGalleryEmbedPart(part) {
  const raw = String(part || "").trim();
  const parsed = parseVideoEmbed(raw);
  if (!parsed) return false;
  if (parsed.provider === "youtube" || parsed.provider === "vimeo") return true;
  if (parsed.provider === "file") return true;
  if (parsed.provider === "drive") {
    return /#(asvideo|video)$/i.test(raw);
  }
  return false;
}

function embedSlideFromParsed(parsed, originalUrl) {
  if (!parsed || !parsed.embedUrl) return null;
  const slide = {
    kind: "embed",
    provider: parsed.provider,
    embedUrl: parsed.embedUrl,
    originalUrl: String(originalUrl || "").trim(),
  };
  if (parsed.provider === "drive") {
    slide.streamUrls = parsed.streamUrls || [parsed.streamUrl, parsed.streamUrlAlt].filter(Boolean);
    slide.driveFileId = parsed.driveFileId;
  }
  return slide;
}

/**
 * Build gallery slides from sheet row keys.
 * Photo: "Photo links" / "Photo link" — pipe or newline. Drive image links use <img>. For a Drive *video* mixed
 * with photos in this cell, append #video to the URL so it embeds.
 * Video: "Video link(s)" / "Video links" / "Video link" — each URL becomes a slide after photos (same gallery).
 * `videos` in the product object is always [] (no duplicate section below).
 */
export function buildProductMediaFromRow(row, legacyPhotoCell, legacyVideoCell) {
  const photoRaw = resolvePhotoRaw(row, legacyPhotoCell);

  const parts = splitSheetMultiValue(photoRaw);
  /** @type {Array<{ kind: 'image', urls: string[] } | { kind: 'embed', provider: string, embedUrl: string, originalUrl: string }>} */
  const galleryItems = [];
  const imageGroups = [];
  for (const part of parts.length ? parts : []) {
    if (isGalleryEmbedPart(part)) {
      const parsed = parseVideoEmbed(stripGalleryUrlHint(part));
      const slide = embedSlideFromParsed(parsed, stripGalleryUrlHint(part));
      if (slide) galleryItems.push(slide);
      continue;
    }
    const urls = driveImageCandidateUrls(stripGalleryUrlHint(part));
    if (urls.length) {
      galleryItems.push({ kind: "image", urls });
      imageGroups.push(urls);
    }
  }

  const videoRaw = resolveVideoRaw(row, legacyVideoCell);

  for (const v of splitSheetMultiValue(videoRaw)) {
    const parsed = parseVideoEmbed(v);
    const slide = embedSlideFromParsed(parsed, v);
    if (slide) galleryItems.push(slide);
  }

  return { imageGroups, galleryItems, videos: [] };
}

/** Shop card / fallback: first image URL list from gallery or imageGroups. */
export function firstImageUrlsForShop(imageGroups, galleryItems) {
  const groups = Array.isArray(imageGroups) ? imageGroups : [];
  if (groups[0]?.length) return groups[0];
  const slide = (Array.isArray(galleryItems) ? galleryItems : []).find(
    (g) => g && g.kind === "image" && Array.isArray(g.urls) && g.urls.length,
  );
  return slide?.urls || [];
}
